import type { CreateSaleItem } from "@repo/shared-types";
import { isSubscriptionRequired } from "@repo/api-client";

const STORAGE_KEY = "pendingSales";

interface PendingSale extends CreateSaleItem {
  timestamp: number;
  retryCount?: number;
}

/**
 * Guarda una venta pendiente en localStorage para reintentar más tarde
 */
export function savePendingSale(saleItem: CreateSaleItem): void {
  try {
    const pendingSales = getPendingSales();
    const newPendingSale: PendingSale = {
      ...saleItem,
      timestamp: Date.now(),
      retryCount: 0,
    };
    pendingSales.push(newPendingSale);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingSales));
  } catch (error) {
    console.error("Error saving pending sale to localStorage:", error);
  }
}

/**
 * Obtiene todas las ventas pendientes del localStorage
 */
function getPendingSales(): PendingSale[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as PendingSale[];
  } catch (error) {
    console.error("Error reading pending sales from localStorage:", error);
    return [];
  }
}

/**
 * Elimina una venta pendiente del localStorage (elimina solo la primera coincidencia)
 */
function removePendingSale(saleItem: CreateSaleItem): void {
  try {
    const pendingSales = getPendingSales();
    const index = pendingSales.findIndex(
      (pending) =>
        pending.productIds.every((productId) =>
          saleItem.productIds.includes(productId)
        ) &&
        pending.playerId === saleItem.playerId &&
        pending.name === saleItem.name
    );
    if (index !== -1) {
      pendingSales.splice(index, 1);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingSales));
    }
  } catch (error) {
    console.error("Error removing pending sale from localStorage:", error);
  }
}

/**
 * Procesa todas las ventas pendientes intentando enviarlas a la API
 * Elimina las ventas del localStorage si se envían exitosamente
 *
 * Con la suscripción del club vencida el API rechaza todas: se detiene en la
 * primera, deja la cola intacta y relanza el error para que el POS muestre el aviso.
 */
export async function processPendingSales(
  createSaleItem: (saleItem: CreateSaleItem) => Promise<any>
): Promise<void> {
  let pendingSales = getPendingSales();
  if (pendingSales.length === 0) return;

  // Procesar las ventas en orden (más antiguas primero)
  const salesToProcess = [...pendingSales].sort(
    (a, b) => a.timestamp - b.timestamp
  );

  for (const sale of salesToProcess) {
    // Verificar si la venta aún existe en localStorage (puede haber sido eliminada)
    const currentPendingSales = getPendingSales();
    const stillExists = currentPendingSales.some(
      (pending) =>
        pending.productIds.every((productId) =>
          sale.productIds.includes(productId)
        ) && pending.timestamp === sale.timestamp
    );

    if (!stillExists) {
      // Ya fue procesada y eliminada, continuar con la siguiente
      continue;
    }

    try {
      await createSaleItem({
        productIds: sale.productIds,
        playerId: sale.playerId,
        name: sale.name,
      });
      // Si se envió exitosamente, eliminar del localStorage
      // Usamos removePendingSale que ahora elimina solo la primera coincidencia
      removePendingSale({
        productIds: sale.productIds,
        playerId: sale.playerId,
        name: sale.name,
      });
    } catch (error) {
      if (isSubscriptionRequired(error)) throw error;
      // Si falla, mantenerla en localStorage para reintentar más tarde
      console.error(
        `Error processing pending sale (productIds: ${sale.productIds.join(
          ", "
        )}):`,
        error
      );
    }
  }
}
